import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReportData, SaveStatus } from '@/types'

const DEBOUNCE_MS = 2000

export function useAutoSave(reportId: string | null) {
  const [data, setData] = useState<ReportData>({})
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dataRef = useRef(data)
  const initializedRef = useRef(false)
  dataRef.current = data

  const storageKey = reportId ? `cmr_report_${reportId}` : null

  // Set initial data from API response (skips triggering a save-back)
  const setInitialData = useCallback((initial: ReportData) => {
    setData(initial)
    initializedRef.current = true
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(initial))
      } catch { /* quota exceeded */ }
    }
  }, [storageKey])

  const onChange = useCallback((id: string, value: string) => {
    setData(prev => {
      const next = { ...prev, [id]: value }
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(next))
        } catch { /* quota exceeded */ }
      }
      return next
    })
  }, [storageKey])

  // Debounced save to API
  useEffect(() => {
    if (!reportId || !initializedRef.current) return

    if (timerRef.current) clearTimeout(timerRef.current)

    timerRef.current = setTimeout(async () => {
      setSaveStatus('saving')
      try {
        const res = await fetch(`/api/reports/${reportId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ data: dataRef.current }),
        })
        if (!res.ok) throw new Error('Save failed')
        setSaveStatus('saved')
        setTimeout(() => setSaveStatus('idle'), 3000)
      } catch {
        setSaveStatus('error')
      }
    }, DEBOUNCE_MS)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [data, reportId])

  return { data, setInitialData, onChange, saveStatus }
}
